import {
  ALLOWED_FILE_CONTENT_TYPES,
  MAX_FILE_BYTES,
  type CreateTravelDocumentRequest,
  type FileCategory,
  type FileSensitivity,
  type Locale,
  type Participant,
  type RecordableConsentPurpose,
  type ShareLink,
  type Translate,
  type TravelDocument,
  type TravelDocumentKind,
  type TravelDocumentWarning,
  type TripFile,
  type UpdateTravelDocumentRequest,
  zoneCityLabel,
} from '@itin/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { useParams } from 'react-router-dom'
import { ConfirmationImport } from '../components/ConfirmationImport.js'
import { MoneyCard } from '../components/MoneyCard.js'
import { PlanEditor } from '../components/PlanEditor.js'
import { SavedPlacesCard } from '../components/SavedPlacesCard.js'
import { SnippetsCard } from '../components/SnippetsCard.js'
import { Button, Card, ErrorNotice, Field, Input, Select, TextArea } from '../components/ui.js'
import { api, ApiError, saveBlob } from '../lib/api.js'
import { formatDate, useLocale, useT } from '../lib/i18n.js'

/**
 * The exemplar screen: the people on a trip, and the links that reach them.
 *
 * The pattern for Phase 2 to copy — one `useQuery` per resource, mutations that
 * invalidate the queries they affect, all text through `t`, and errors rendered
 * from codes rather than from network text.
 */
export function TripPage() {
  const { tripId = '' } = useParams()
  const t = useT()
  const { locale } = useLocale()

  const trip = useQuery({ queryKey: ['trip', tripId], queryFn: () => api.getTrip(tripId) })

  return (
    <div className="flex flex-col gap-4 sm:gap-6">
      {trip.isError && <ErrorNotice error={trip.error} />}
      {trip.data && (
        <header className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold">{trip.data.title}</h1>
          <p className="text-sm text-slate-500">
            {t('trips.dates', {
              start: formatDate(trip.data.startDate, locale),
              end: formatDate(trip.data.endDate, locale),
            })}
            {' · '}
            {/* The city, not `America/Sao_Paulo`. The identifier is how the
                zone is stored, never how it is shown. */}
            {zoneCityLabel(trip.data.timezone, locale)}
          </p>
        </header>
      )}

      {/*
        The itinerary is first, and everything else folds.

        It used to sit second, under a two-line textarea for emergency contacts
        that Ana fills once per trip and then never opens again — so on a phone
        the first screenful of every visit was a field she had already finished,
        and the plan she came to work on started below the fold. The order here
        is how often she touches a thing, and `collapsible` does the rest.
      */}
      {trip.data && <PlanEditor trip={trip.data} />}
      {/* Directly under the itinerary, because it is a way of filling that in
          rather than a thing of its own — and above the cards she opens once a
          trip, because she pastes a confirmation every time one arrives. */}
      {trip.data && <ConfirmationImport trip={trip.data} mode="notes" />}
      {trip.data && <ConfirmationImport trip={trip.data} />}
      {trip.data && <EmergencyCard trip={trip.data} />}
      {trip.data && <SavedPlacesCard trip={trip.data} />}
      {/* Beside the places library, because they are the same idea about
          different content and she reaches for them at the same moment. */}
      {trip.data && <SnippetsCard trip={trip.data} />}
      <MoneyCard tripId={tripId} />
      <ParticipantsCard tripId={tripId} />
      <TravelDocumentsCard tripId={tripId} />
      <ShareLinksCard tripId={tripId} />
      <DocumentsCard tripId={tripId} />
    </div>
  )
}

/** The block that renders above everything on the traveller's page. */
function EmergencyCard({ trip }: { trip: import('@itin/shared').Trip }) {
  const t = useT()
  const queryClient = useQueryClient()
  const [value, setValue] = useState(trip.emergencyInfo ?? '')

  const save = useMutation({
    mutationFn: () =>
      api.updateTrip(trip.id, { emergencyInfo: value.trim() === '' ? null : value }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['trip', trip.id] }),
  })

  return (
    <Card title={t('plan.emergency')} collapsible>
      <div className="flex flex-col gap-2">
        <Field
          label={t('trips.field.emergencyInfo')}
          hint={t('trips.field.emergencyInfoHint')}
          guidance="trips.field.emergencyInfo.guidance"
        >
          <TextArea
            name="emergencyInfo"
            rows={2}
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        </Field>
        {save.isError && <ErrorNotice error={save.error} />}
        {value !== (trip.emergencyInfo ?? '') && (
          <div>
            <Button onClick={() => save.mutate()} disabled={save.isPending}>
              {t('common.save')}
            </Button>
          </div>
        )}
      </div>
    </Card>
  )
}

function ParticipantsCard({ tripId }: { tripId: string }) {
  const t = useT()
  const queryClient = useQueryClient()
  const [adding, setAdding] = useState(false)

  const participants = useQuery({
    queryKey: ['participants', tripId],
    queryFn: () => api.listParticipants(tripId),
  })

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['participants', tripId] }),
      queryClient.invalidateQueries({ queryKey: ['shareLinks', tripId] }),
    ])

  const create = useMutation({
    mutationFn: (input: { displayName: string; role: 'advisor' | 'traveller'; email?: string; phone?: string }) =>
      api.createParticipant(tripId, input),
    async onSuccess() {
      await invalidate()
      setAdding(false)
    },
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteParticipant(id),
    onSuccess: invalidate,
  })

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    const email = String(form.get('email') ?? '').trim()
    const phone = String(form.get('phone') ?? '').trim()
    create.mutate({
      displayName: String(form.get('displayName')),
      role: String(form.get('role')) as 'advisor' | 'traveller',
      ...(email ? { email } : {}),
      ...(phone ? { phone } : {}),
    })
  }

  return (
    <Card
      testId="participants-card"
      title={t('participants.title')}
      collapsible
      actions={
        <Button variant="quiet" onClick={() => setAdding((v) => !v)}>
          {t('participants.add')}
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        {/*
          Stated in the interface, not just in the schema. Ana needs to know the
          name she types is the name that will appear against every action —
          the person opening the link cannot change it.
        */}
        <p className="text-xs text-slate-500">{t('participants.nameNote')}</p>

        {participants.isPending && <p className="text-sm text-slate-500">{t('common.loading')}</p>}
        {participants.isError && <ErrorNotice error={participants.error} />}
        {participants.data?.length === 0 && (
          <p className="text-sm text-slate-500">{t('participants.empty')}</p>
        )}

        <ul className="flex flex-col divide-y divide-slate-200">
          {participants.data?.map((participant) => (
            <ParticipantRow
              key={participant.id}
              participant={participant}
              onRemove={() => remove.mutate(participant.id)}
            />
          ))}
        </ul>

        {adding && (
          <form onSubmit={submit} className="flex flex-col gap-4 border-t border-slate-200 pt-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label={t('participants.field.displayName')}
                guidance="participants.field.displayName.guidance"
              >
                <Input name="displayName" required maxLength={120} autoFocus />
              </Field>
              <Field label={t('participants.field.role')} guidance="participants.field.role.guidance">
                <Select name="role" defaultValue="traveller">
                  <option value="traveller">{t('participants.role.traveller')}</option>
                  <option value="advisor">{t('participants.role.advisor')}</option>
                </Select>
              </Field>
              <Field label={t('participants.field.email')} guidance="participants.field.email.guidance">
                <Input name="email" type="email" />
              </Field>
              <Field
                label={t('participants.field.phone')}
                hint="+55 11 98765 4321"
                guidance="participants.field.phone.guidance"
              >
                <Input name="phone" inputMode="tel" placeholder="+5511987654321" />
              </Field>
            </div>
            {create.isError && <ErrorNotice error={create.error} />}
            <div className="flex gap-2">
              <Button type="submit" disabled={create.isPending}>
                {t('common.save')}
              </Button>
              <Button type="button" variant="quiet" onClick={() => setAdding(false)}>
                {t('common.cancel')}
              </Button>
            </div>
          </form>
        )}
      </div>
    </Card>
  )
}

function ParticipantRow({
  participant,
  onRemove,
}: {
  participant: Participant
  onRemove(): void
}) {
  const t = useT()
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(participant.displayName)

  const rename = useMutation({
    mutationFn: () => api.updateParticipant(participant.id, { displayName: name }),
    async onSuccess() {
      await queryClient.invalidateQueries({ queryKey: ['participants', participant.tripId] })
      setEditing(false)
    },
  })

  return (
    <li className="flex flex-col gap-2 py-3">
      <div className="flex items-center justify-between gap-4">
        {editing ? (
          <form
            className="flex flex-1 items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault()
              rename.mutate()
            }}
          >
            <Input value={name} onChange={(e) => setName(e.target.value)} className="flex-1" autoFocus />
            <Button type="submit" disabled={rename.isPending}>
              {t('common.save')}
            </Button>
            <Button type="button" variant="quiet" onClick={() => setEditing(false)}>
              {t('common.cancel')}
            </Button>
          </form>
        ) : (
          <>
            <div className="flex flex-col">
              <span className="font-medium">{participant.displayName}</span>
              <span className="text-xs text-slate-500">
                {t(`participants.role.${participant.role}`)}
                {participant.email ? ` · ${participant.email}` : ''}
              </span>
            </div>
            <div className="flex gap-2">
              <Button variant="quiet" onClick={() => setEditing(true)}>
                {t('common.rename')}
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  if (confirm(t('participants.removeConfirm', { name: participant.displayName }))) {
                    onRemove()
                  }
                }}
              >
                {t('common.remove')}
              </Button>
            </div>
          </>
        )}
      </div>

      {/*
        The consent moment lives right here, on the participant form, rather
        than as a checkbox buried in terms of service. Recording a health,
        dietary or accessibility note and consenting to it are the same act —
        see consent.service.ts for why that is structural rather than a UI
        convention.
      */}
      <ParticipantConsents participant={participant} />
    </li>
  )
}

const TRAVEL_DOCUMENT_KINDS: readonly TravelDocumentKind[] = ['passport', 'id_card', 'visa']

/**
 * Build unit 7.16 — travel document details. Fields, never images.
 *
 * The form below asks for exactly the six fields Ana needs to ticket a trip.
 * There is no file input anywhere in this component and there must never be
 * one: a passport photo is only how those fields travel, and PATTERNS.md §6
 * / the build spec are explicit that no image is ever stored. If in-app
 * submission from a share link is ever added, the fields still go straight
 * here — never through an upload.
 */
function TravelDocumentsCard({ tripId }: { tripId: string }) {
  const t = useT()
  const { locale } = useLocale()
  const queryClient = useQueryClient()

  const participants = useQuery({
    queryKey: ['participants', tripId],
    queryFn: () => api.listParticipants(tripId),
  })
  const documents = useQuery({
    queryKey: ['travelDocuments', tripId],
    queryFn: () => api.listTravelDocumentsForTrip(tripId),
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['travelDocuments', tripId] })

  const create = useMutation({
    mutationFn: ({
      participantId,
      input,
    }: {
      participantId: string
      input: CreateTravelDocumentRequest
    }) => api.createTravelDocument(participantId, input),
    onSuccess: invalidate,
  })
  const update = useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateTravelDocumentRequest }) =>
      api.updateTravelDocument(id, input),
    onSuccess: invalidate,
  })
  const remove = useMutation({
    mutationFn: (id: string) => api.deleteTravelDocument(id),
    onSuccess: invalidate,
  })

  const documentsByParticipant = new Map<string, TravelDocument[]>()
  for (const doc of documents.data ?? []) {
    const list = documentsByParticipant.get(doc.participantId) ?? []
    list.push(doc)
    documentsByParticipant.set(doc.participantId, list)
  }

  return (
    <Card title={t('travelDocuments.title')} collapsible>
      <div className="flex flex-col gap-4">
        <p className="text-xs text-slate-500">{t('travelDocuments.noImageNote')}</p>

        {participants.isError && <ErrorNotice error={participants.error} />}
        {documents.isError && <ErrorNotice error={documents.error} />}
        {create.isError && <ErrorNotice error={create.error} />}
        {update.isError && <ErrorNotice error={update.error} />}
        {remove.isError && <ErrorNotice error={remove.error} />}

        {participants.data?.length === 0 && (
          <p className="text-sm text-slate-500">{t('participants.empty')}</p>
        )}

        <ul className="flex flex-col divide-y divide-slate-200">
          {participants.data?.map((participant) => (
            <li key={participant.id} className="py-3">
              <ParticipantDocuments
                participant={participant}
                documents={documentsByParticipant.get(participant.id) ?? []}
                locale={locale}
                creating={create.isPending}
                updating={update.isPending}
                onCreate={(input) => create.mutate({ participantId: participant.id, input })}
                onUpdate={(id, input) => update.mutate({ id, input })}
                onRemove={(id) => remove.mutate(id)}
              />
            </li>
          ))}
        </ul>
      </div>
    </Card>
  )
}

function ParticipantDocuments({
  participant,
  documents,
  locale,
  creating,
  updating,
  onCreate,
  onUpdate,
  onRemove,
}: {
  participant: Participant
  documents: TravelDocument[]
  locale: Locale
  creating: boolean
  updating: boolean
  onCreate(input: CreateTravelDocumentRequest): void
  onUpdate(id: string, input: UpdateTravelDocumentRequest): void
  onRemove(id: string): void
}) {
  const t = useT()
  const [adding, setAdding] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)

  const existingKinds = new Set(documents.map((d) => d.kind))
  const availableKinds = TRAVEL_DOCUMENT_KINDS.filter((k) => !existingKinds.has(k))

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="font-medium">{participant.displayName}</span>
        {!adding && availableKinds.length > 0 && (
          <Button variant="quiet" onClick={() => setAdding(true)}>
            {t('travelDocuments.add')}
          </Button>
        )}
      </div>

      {documents.length === 0 && !adding && (
        <p className="text-sm text-slate-500">{t('travelDocuments.empty')}</p>
      )}

      {documents.map((doc) =>
        editingId === doc.id ? (
          <DocumentForm
            key={doc.id}
            kindOptions={[doc.kind]}
            existing={doc}
            submitting={updating}
            onCancel={() => setEditingId(null)}
            onSubmit={(input) => {
              // `existing` is set, so `DocumentForm` never includes `kind` or a
              // null `purgeAfter` in what it builds — the union prop type just
              // cannot express that narrowing.
              onUpdate(doc.id, input as UpdateTravelDocumentRequest)
              setEditingId(null)
            }}
          />
        ) : (
          <div
            key={doc.id}
            className="flex flex-col gap-2 rounded-md border border-slate-200 p-3"
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{t(`travelDocuments.kind.${doc.kind}`)}</span>
              <div className="flex gap-2">
                <Button variant="quiet" onClick={() => setEditingId(doc.id)}>
                  {t('travelDocuments.edit')}
                </Button>
                <Button
                  variant="danger"
                  onClick={() => {
                    if (confirm(t('travelDocuments.removeConfirm'))) onRemove(doc.id)
                  }}
                >
                  {t('common.remove')}
                </Button>
              </div>
            </div>

            <dl className="grid grid-cols-1 gap-1 text-sm text-slate-600 sm:grid-cols-2">
              <DocDetail label={t('travelDocuments.field.fullNameAsPrinted')} value={doc.fullNameAsPrinted} />
              <DocDetail label={t('travelDocuments.field.documentNumber')} value={doc.documentNumber} />
              {doc.nationality && (
                <DocDetail label={t('travelDocuments.field.nationality')} value={doc.nationality} />
              )}
              {doc.issuingCountry && (
                <DocDetail label={t('travelDocuments.field.issuingCountry')} value={doc.issuingCountry} />
              )}
              {doc.dateOfBirth && (
                <DocDetail
                  label={t('travelDocuments.field.dateOfBirth')}
                  value={formatDate(doc.dateOfBirth, locale)}
                />
              )}
              {doc.expiresOn && (
                <DocDetail
                  label={t('travelDocuments.field.expiresOn')}
                  value={formatDate(doc.expiresOn, locale)}
                />
              )}
            </dl>
            <p className="text-xs text-slate-400">
              {t('travelDocuments.field.purgeAfter')}: {formatDate(doc.purgeAfter.slice(0, 10), locale)}
            </p>

            {doc.warnings.length > 0 && (
              <ul className="flex flex-col gap-1">
                {doc.warnings.map((warning, index) => (
                  <li
                    key={index}
                    className="rounded-md bg-amber-50 px-2 py-1 text-xs text-amber-800"
                  >
                    {renderWarning(t, locale, warning)}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ),
      )}

      {adding && (
        <DocumentForm
          kindOptions={availableKinds.length > 0 ? availableKinds : ['passport']}
          submitting={creating}
          onCancel={() => setAdding(false)}
          onSubmit={(input) => {
            onCreate(input as CreateTravelDocumentRequest)
            setAdding(false)
          }}
        />
      )}
    </div>
  )
}

function DocDetail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="inline text-slate-400">{label}: </dt>
      <dd className="inline">{value}</dd>
    </div>
  )
}

const RECORDABLE_PURPOSES = ['health', 'dietary', 'accessibility'] as const

function formatMoment(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(iso),
  )
}

/**
 * The consent moment, on the participant form.
 *
 * One row per purpose (health/dietary/accessibility), because
 * `participant_sensitive_notes` holds exactly one note per (participant,
 * purpose) — `unique(participant_id, purpose)`. Recording and saving are two
 * requests (`api.recordConsent` then `api.saveSensitiveNote`), not one, but
 * from the advisor's side they are a single submit: type the note, read the
 * consent wording it renders below the textarea, tick the confirmation, and
 * the one button does both. See `consent.service.ts` for why the two calls
 * are not wrapped in a shared transaction.
 */
function ParticipantConsents({ participant }: { participant: Participant }) {
  const t = useT()
  const { locale } = useLocale()
  const queryClient = useQueryClient()
  const session = useQuery({ queryKey: ['me'], queryFn: api.me })

  const [editing, setEditing] = useState<RecordableConsentPurpose | null>(null)
  const [body, setBody] = useState('')
  const [confirmed, setConfirmed] = useState(false)

  const consents = useQuery({
    queryKey: ['consents', participant.id],
    queryFn: () => api.listConsents(participant.id),
  })
  const notes = useQuery({
    queryKey: ['sensitiveNotes', participant.id],
    queryFn: () => api.listSensitiveNotes(participant.id),
  })

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['consents', participant.id] }),
      queryClient.invalidateQueries({ queryKey: ['sensitiveNotes', participant.id] }),
    ])

  const recordAndSave = useMutation({
    mutationFn: async (purpose: RecordableConsentPurpose) => {
      await api.recordConsent(participant.id, { purpose, locale })
      return api.saveSensitiveNote(participant.id, purpose, { body: body.trim() })
    },
    async onSuccess() {
      await invalidate()
      setEditing(null)
      setBody('')
      setConfirmed(false)
    },
  })

  const withdraw = useMutation({
    mutationFn: (id: string) => api.withdrawConsent(id),
    onSuccess: invalidate,
  })

  function startEditing(purpose: RecordableConsentPurpose, currentBody: string | null) {
    setEditing(purpose)
    setBody(currentBody ?? '')
    setConfirmed(false)
  }

  const trimmedBody = body.trim()
  const preview =
    editing && trimmedBody !== ''
      ? t(`consent.statement.${editing}`, {
          participant: participant.displayName,
          agency: session.data?.user.agencyName ?? '',
        })
      : null

  return (
    <div className="flex flex-col gap-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
      <span className="font-medium">{t('consent.title')}</span>
      <p className="text-xs text-slate-500">{t('consent.hint')}</p>

      {consents.isError && <ErrorNotice error={consents.error} />}
      {notes.isError && <ErrorNotice error={notes.error} />}

      <ul className="flex flex-col gap-2">
        {RECORDABLE_PURPOSES.map((purpose) => {
          const note = notes.data?.find((n) => n.purpose === purpose)
          const activeConsent = consents.data?.find(
            (c) => c.purpose === purpose && c.withdrawnAt === null,
          )
          const isEditing = editing === purpose

          return (
            <li
              key={purpose}
              className="flex flex-col gap-2 rounded-md border border-slate-200 bg-white p-2"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {t(`consent.purpose.${purpose}`)}
                </span>
                <div className="flex gap-2">
                  {activeConsent && note?.body != null && (
                    <Button
                      variant="danger"
                      onClick={() => {
                        if (confirm(t('consent.withdrawConfirm'))) withdraw.mutate(activeConsent.id)
                      }}
                    >
                      {t('consent.withdraw')}
                    </Button>
                  )}
                  {!isEditing && (
                    <Button
                      variant="quiet"
                      onClick={() => startEditing(purpose, note?.body ?? null)}
                    >
                      {note ? t('consent.edit') : t('consent.add')}
                    </Button>
                  )}
                </div>
              </div>

              {note &&
                (note.body !== null ? (
                  <p>{note.body}</p>
                ) : (
                  <p className="italic text-slate-400">{t('consent.noteUnreadable')}</p>
                ))}
              {!note && !isEditing && <p className="text-xs text-slate-500">{t('consent.empty')}</p>}

              {activeConsent && note?.body != null && (
                <span className="text-xs text-slate-500">
                  {t('consent.grantedOn', { date: formatMoment(activeConsent.grantedAt, locale) })}
                </span>
              )}

              {isEditing && (
                <form
                  className="flex flex-col gap-3 border-t border-slate-200 pt-3"
                  onSubmit={(e) => {
                    e.preventDefault()
                    recordAndSave.mutate(purpose)
                  }}
                >
                  <Field
                    label={t('consent.field.detail')}
                    hint={t('consent.field.detailHint')}
                    guidance="consent.field.detail.guidance"
                  >
                    <TextArea
                      rows={2}
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      autoFocus
                    />
                  </Field>
                  {preview && (
                    <div className="rounded-md bg-amber-50 p-2 text-xs text-amber-900">
                      <p className="mb-1 font-medium">{t('consent.preview')}</p>
                      <p>{preview}</p>
                    </div>
                  )}
                  <label className="flex items-start gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={confirmed}
                      onChange={(e) => setConfirmed(e.target.checked)}
                      className="mt-0.5"
                    />
                    {t('consent.confirm')}
                  </label>
                  {recordAndSave.isError && <ErrorNotice error={recordAndSave.error} />}
                  <div className="flex gap-2">
                    <Button
                      type="submit"
                      disabled={recordAndSave.isPending || !confirmed || trimmedBody === ''}
                    >
                      {t('consent.record')}
                    </Button>
                    <Button type="button" variant="quiet" onClick={() => setEditing(null)}>
                      {t('common.cancel')}
                    </Button>
                  </div>
                </form>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

/** Renders one warning's text from its code and params — never raw server text. */
function renderWarning(t: Translate, locale: Locale, warning: TravelDocumentWarning): string {
  const params = warning.params ?? {}
  switch (warning.code) {
    case 'expired_before_return':
      return t('travelDocuments.warning.expired_before_return', {
        expiresOn: formatDate(params['expiresOn'] ?? '', locale),
        returnDate: formatDate(params['returnDate'] ?? '', locale),
      })
    case 'expires_within_six_months':
      return t('travelDocuments.warning.expires_within_six_months', {
        expiresOn: formatDate(params['expiresOn'] ?? '', locale),
        returnDate: formatDate(params['returnDate'] ?? '', locale),
      })
    case 'name_mismatch':
      return t('travelDocuments.warning.name_mismatch', {
        printedName: params['printedName'] ?? '',
        displayName: params['displayName'] ?? '',
      })
  }
}

/**
 * One form, shared by "add a new document" and "edit an existing one". The
 * kind selector only appears when adding — changing the kind of an existing
 * document would change which unique slot it occupies, so editing keeps it
 * fixed.
 */
function DocumentForm({
  kindOptions,
  existing,
  submitting,
  onCancel,
  onSubmit,
}: {
  kindOptions: readonly TravelDocumentKind[]
  existing?: TravelDocument
  submitting: boolean
  onCancel(): void
  onSubmit(input: CreateTravelDocumentRequest | UpdateTravelDocumentRequest): void
}) {
  const t = useT()

  return (
    <form
      className="flex flex-col gap-4 rounded-md border border-slate-200 p-3"
      onSubmit={(event) => {
        event.preventDefault()
        const form = new FormData(event.currentTarget)
        const get = (name: string) => String(form.get(name) ?? '').trim()
        const nationality = get('nationality').toUpperCase()
        const issuingCountry = get('issuingCountry').toUpperCase()
        const dateOfBirth = get('dateOfBirth')
        const expiresOn = get('expiresOn')
        onSubmit({
          ...(existing ? {} : { kind: get('kind') as TravelDocumentKind }),
          fullNameAsPrinted: get('fullNameAsPrinted'),
          documentNumber: get('documentNumber'),
          nationality: nationality ? nationality : null,
          issuingCountry: issuingCountry ? issuingCountry : null,
          dateOfBirth: dateOfBirth ? dateOfBirth : null,
          expiresOn: expiresOn ? expiresOn : null,
        })
      }}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        {!existing && (
          <Field
            label={t('travelDocuments.field.kind')}
            guidance="travelDocuments.field.kind.guidance"
          >
            <Select name="kind" defaultValue={kindOptions[0]}>
              {kindOptions.map((kind) => (
                <option key={kind} value={kind}>
                  {t(`travelDocuments.kind.${kind}`)}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Field
          label={t('travelDocuments.field.fullNameAsPrinted')}
          hint={t('travelDocuments.field.fullNameAsPrintedHint')}
        >
          <Input
            name="fullNameAsPrinted"
            required
            maxLength={200}
            defaultValue={existing?.fullNameAsPrinted}
            autoFocus
          />
        </Field>
        <Field
          label={t('travelDocuments.field.documentNumber')}
          guidance="travelDocuments.field.documentNumber.guidance"
        >
          <Input name="documentNumber" required maxLength={64} defaultValue={existing?.documentNumber} />
        </Field>
        <Field
          label={t('travelDocuments.field.nationality')}
          hint={t('travelDocuments.countryHint')}
          guidance="travelDocuments.field.nationality.guidance"
        >
          <Input
            name="nationality"
            maxLength={2}
            defaultValue={existing?.nationality ?? ''}
            className="uppercase"
          />
        </Field>
        <Field
          label={t('travelDocuments.field.issuingCountry')}
          hint={t('travelDocuments.countryHint')}
          guidance="travelDocuments.field.issuingCountry.guidance"
        >
          <Input
            name="issuingCountry"
            maxLength={2}
            defaultValue={existing?.issuingCountry ?? ''}
            className="uppercase"
          />
        </Field>
        <Field
          label={t('travelDocuments.field.dateOfBirth')}
          guidance="travelDocuments.field.dateOfBirth.guidance"
        >
          <Input name="dateOfBirth" type="date" defaultValue={existing?.dateOfBirth ?? ''} />
        </Field>
        <Field
          label={t('travelDocuments.field.expiresOn')}
          guidance="travelDocuments.field.expiresOn.guidance"
        >
          <Input name="expiresOn" type="date" defaultValue={existing?.expiresOn ?? ''} />
        </Field>
      </div>
      <div className="flex gap-2">
        <Button type="submit" disabled={submitting}>
          {t('common.save')}
        </Button>
        <Button type="button" variant="quiet" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  )
}

function ShareLinksCard({ tripId }: { tripId: string }) {
  const t = useT()
  const queryClient = useQueryClient()

  const links = useQuery({
    queryKey: ['shareLinks', tripId],
    queryFn: () => api.listShareLinks(tripId),
  })
  const participants = useQuery({
    queryKey: ['participants', tripId],
    queryFn: () => api.listParticipants(tripId),
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['shareLinks', tripId] })

  const [newLabel, setNewLabel] = useState('')

  const create = useMutation({
    mutationFn: (participantId: string | null) =>
      api.createShareLink(tripId, {
        ...(participantId ? { participantId } : {}),
        ...(newLabel.trim() ? { label: newLabel.trim() } : {}),
      }),
    async onSuccess() {
      await invalidate()
      setNewLabel('')
    },
  })

  const rename = useMutation({
    mutationFn: ({ id, label }: { id: string; label: string | null }) =>
      api.renameShareLink(id, label),
    onSuccess: invalidate,
  })
  const revoke = useMutation({
    mutationFn: (id: string) => api.revokeShareLink(id),
    onSuccess: invalidate,
  })

  const nameFor = (participantId: string | null) =>
    participants.data?.find((p) => p.id === participantId)?.displayName

  /**
   * Revoked links are hidden, not deleted.
   *
   * A revoked link is a dead URL somebody may still be holding, so the record
   * has to stay — but it is finished business, and leaving it in the list means
   * the live link Ana wants to send is buried among links that no longer work.
   * That is a copy-the-wrong-one waiting to happen.
   */
  const [showRevoked, setShowRevoked] = useState(false)
  const all = links.data ?? []
  const live = all.filter((l) => !l.revokedAt)
  const revoked = all.filter((l) => l.revokedAt)
  const shown = showRevoked ? [...live, ...revoked] : live

  return (
    <Card
      testId="share-links-card"
      title={t('share.title')}
      collapsible
      actions={
        <div className="flex items-center gap-2">
          {/*
            Naming happens where the link is made, not in a dialog afterwards.
            Optional: an unnamed link is a perfectly good link, and forcing a
            name on the common case would be worse than the placeholder text
            this replaces.
          */}
          <Input
            value={newLabel}
            aria-label={t('share.nameIt')}
            placeholder={t('share.namePlaceholder')}
            maxLength={120}
            className="w-56"
            onChange={(event) => setNewLabel(event.target.value)}
          />
          <Button variant="quiet" onClick={() => create.mutate(null)} disabled={create.isPending}>
            {t('share.create')}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {links.isError && <ErrorNotice error={links.error} />}
        {create.isError && <ErrorNotice error={create.error} />}
        {all.length === 0 && <p className="text-sm text-slate-500">{t('share.empty')}</p>}
        {all.length > 0 && live.length === 0 && !showRevoked && (
          <p className="text-sm text-slate-500">{t('share.allRevoked')}</p>
        )}

        <ul className="flex flex-col divide-y divide-slate-200">
          {shown.map((link) => (
            <ShareLinkRow
              key={link.id}
              link={link}
              participantName={nameFor(link.participantId)}
              busy={rename.isPending}
              onRename={(label) => rename.mutate({ id: link.id, label })}
              onRevoke={() => revoke.mutate(link.id)}
            />
          ))}
        </ul>

        {revoked.length > 0 && (
          <div>
            <button
              type="button"
              onClick={() => setShowRevoked((v) => !v)}
              className="-mx-2 flex min-h-11 items-center px-2 text-sm text-slate-500 underline decoration-slate-300 underline-offset-4 hover:text-slate-800 sm:mx-0 sm:min-h-0 sm:px-0"
            >
              {showRevoked
                ? t('share.hideRevoked')
                : t('share.showRevoked', { count: revoked.length })}
            </button>
          </div>
        )}

        <p className="text-xs text-slate-500">{t('share.nameHint')}</p>

        {participants.data && participants.data.length > 0 && (
          <div className="flex flex-wrap gap-2 border-t border-slate-200 pt-4">
            {participants.data.map((participant) => (
              <Button
                key={participant.id}
                variant="quiet"
                disabled={create.isPending}
                onClick={() => create.mutate(participant.id)}
              >
                {t('share.createFor', { name: participant.displayName })}
              </Button>
            ))}
          </div>
        )}
      </div>
    </Card>
  )
}

function ShareLinkRow({
  link,
  participantName,
  busy,
  onRename,
  onRevoke,
}: {
  link: ShareLink
  participantName: string | undefined
  busy: boolean
  onRename(label: string | null): void
  onRevoke(): void
}) {
  const t = useT()
  const [copied, setCopied] = useState(false)
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(link.label ?? '')

  return (
    <li className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 flex-col">
        {/*
          A name only when there IS one, and it is Ana's label if she gave one.
          
          The slot used to always render something — "Not named", then "Anyone
          with this link" — and a label that reads the same on every row is a
          label nobody reads. Her own words come first because she chose them;
          the participant binding is the fallback because the product chose it.
        */}
        {editing ? (
          <span className="flex items-center gap-2">
            <Input
              value={draft}
              autoFocus
              maxLength={120}
              aria-label={t('share.nameIt')}
              placeholder={t('share.namePlaceholder')}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  onRename(draft.trim() || null)
                  setEditing(false)
                }
                if (event.key === 'Escape') {
                  setDraft(link.label ?? '')
                  setEditing(false)
                }
              }}
            />
            <Button
              variant="quiet"
              size="sm"
              disabled={busy}
              onClick={() => {
                onRename(draft.trim() || null)
                setEditing(false)
              }}
            >
              {t('common.save')}
            </Button>
          </span>
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="-mx-2 flex min-h-11 items-center self-start px-2 text-left text-sm font-medium text-slate-800 underline decoration-slate-300 decoration-dashed underline-offset-4 hover:decoration-gold sm:mx-0 sm:min-h-0 sm:px-0"
          >
            {link.label ??
              (participantName
                ? t('share.boundTo', { name: participantName })
                : t('share.nameIt'))}
          </button>
        )}
        {/*
          The URL comes from the server, assembled from PUBLIC_BASE_URL. The
          client never builds it from window.location — a page served from a
          staging host would otherwise hand out links nobody can open.
        */}
        <code
          data-testid="share-link-url"
          className={`truncate text-xs ${link.revokedAt ? "text-slate-400 line-through" : "text-slate-500"}`}
        >
          {link.url}
        </code>
      </div>
      <div className="flex shrink-0 gap-2">
        <Button
          variant="quiet"
          onClick={async () => {
            await navigator.clipboard.writeText(link.url)
            setCopied(true)
            setTimeout(() => setCopied(false), 1500)
          }}
        >
          {copied ? t('common.copied') : t('common.copy')}
        </Button>
        {!link.revokedAt && (
          <Button
            variant="danger"
            onClick={() => {
              if (confirm(t('share.revokeConfirm'))) onRevoke()
            }}
          >
            {t('share.revoke')}
          </Button>
        )}
      </div>
    </li>
  )
}

/** '2.4 MB', never a raw byte count — Ana is reading this, not debugging it. */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  const units = ['KB', 'MB', 'GB']
  let value = bytes / 1024
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }
  return `${value.toFixed(1)} ${units[unit]}`
}

function DocumentsCard({ tripId }: { tripId: string }) {
  const t = useT()
  const { locale } = useLocale()
  const queryClient = useQueryClient()
  const [uploadError, setUploadError] = useState<string | null>(null)

  const files = useQuery({ queryKey: ['files', tripId], queryFn: () => api.listFiles(tripId) })
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['files', tripId] })

  const upload = useMutation({
    mutationFn: (input: { file: File; category: FileCategory; sensitivity: FileSensitivity; unlockCode?: string }) =>
      api.uploadFile(tripId, input.file, input),
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteFile(id),
    onSuccess: invalidate,
  })

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setUploadError(null)
    // Captured now rather than read from `event.currentTarget` inside the
    // mutation's `onSuccess` — React nulls that out once the handler returns,
    // and the reset has to happen after an awaited network round trip.
    const formEl = event.currentTarget
    const form = new FormData(formEl)
    const fileInput = (formEl.elements.namedItem('file') as HTMLInputElement | null)?.files?.[0]
    if (!fileInput) return

    // Client-side hints only — the server enforces both again and is the only
    // side that actually matters. This just saves a round trip for the common
    // mistake of picking the wrong file.
    if (!ALLOWED_FILE_CONTENT_TYPES.includes(fileInput.type as (typeof ALLOWED_FILE_CONTENT_TYPES)[number])) {
      setUploadError(t('files.badType'))
      return
    }
    if (fileInput.size > MAX_FILE_BYTES) {
      setUploadError(t('files.tooLarge'))
      return
    }

    const sensitivity = String(form.get('sensitivity')) as FileSensitivity
    const unlockCode = String(form.get('unlockCode') ?? '').trim()
    upload.mutate(
      {
        file: fileInput,
        category: String(form.get('category')) as FileCategory,
        sensitivity,
        ...(unlockCode ? { unlockCode } : {}),
      },
      {
        onSuccess: () => {
          formEl.reset()
          setSensitivityChoice('sensitive')
        },
      },
    )
  }

  const [sensitivityChoice, setSensitivityChoice] = useState<FileSensitivity>('sensitive')

  return (
    <Card title={t('files.title')} collapsible>
      <div className="flex flex-col gap-4">
        {files.isPending && <p className="text-sm text-slate-500">{t('common.loading')}</p>}
        {files.isError && <ErrorNotice error={files.error} />}
        {files.data?.length === 0 && <p className="text-sm text-slate-500">{t('files.empty')}</p>}

        <ul className="flex flex-col divide-y divide-slate-200">
          {files.data?.map((file) => (
            <FileRow
              key={file.id}
              file={file}
              locale={locale}
              onRemove={() => {
                if (confirm(t('files.removeConfirm', { name: file.filename }))) remove.mutate(file.id)
              }}
            />
          ))}
        </ul>

        <form
          onSubmit={submit}
          className="flex flex-col gap-4 border-t border-slate-200 pt-4"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('files.field.file')} guidance="files.field.file.guidance">
              <input
                type="file"
                name="file"
                required
                accept={ALLOWED_FILE_CONTENT_TYPES.join(',')}
                className="text-sm"
              />
            </Field>
            <Field label={t('files.field.category')} guidance="files.field.category.guidance">
              <Select name="category" defaultValue="other">
                <option value="voucher">{t('files.category.voucher')}</option>
                <option value="insurance">{t('files.category.insurance')}</option>
                <option value="ticket">{t('files.category.ticket')}</option>
                <option value="other">{t('files.category.other')}</option>
              </Select>
            </Field>
            <Field label={t('files.field.sensitivity')} guidance="files.field.sensitivity.guidance">
              <Select
                name="sensitivity"
                defaultValue="sensitive"
                onChange={(e) => setSensitivityChoice(e.target.value as FileSensitivity)}
              >
                <option value="sensitive">{t('files.sensitivity.sensitive')}</option>
                <option value="shareable">{t('files.sensitivity.shareable')}</option>
              </Select>
            </Field>
            {sensitivityChoice === 'sensitive' && (
              <Field
                label={t('files.field.unlockCode')}
                hint={t('files.field.unlockCodeHint')}
                guidance="files.field.unlockCode.guidance"
              >
                <Input name="unlockCode" required minLength={4} maxLength={64} />
              </Field>
            )}
          </div>
          {uploadError && <p className="text-sm text-red-600">{uploadError}</p>}
          {upload.isError && <ErrorNotice error={upload.error} />}
          <div>
            <Button type="submit" disabled={upload.isPending}>
              {upload.isPending ? t('files.uploading') : t('files.upload')}
            </Button>
          </div>
        </form>
      </div>
    </Card>
  )
}

function FileRow({
  file,
  locale,
  onRemove,
}: {
  file: TripFile
  locale: ReturnType<typeof useLocale>['locale']
  onRemove(): void
}) {
  const t = useT()
  const [unlocking, setUnlocking] = useState(false)
  const [code, setCode] = useState('')

  const download = useMutation({
    mutationFn: (unlockCode: string | undefined) => api.downloadFile(file.id, unlockCode),
    onSuccess: (blob) => {
      saveBlob(blob, file.filename)
      setUnlocking(false)
      setCode('')
    },
  })

  return (
    <li className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 flex-col">
        <span className="font-medium">{file.filename}</span>
        <span className="text-xs text-slate-500">
          {t(`files.category.${file.category}`)} · {formatBytes(file.byteSize)}
          {file.sensitivity === 'sensitive' && ` · ${t('files.sensitiveBadge')}`}
          {file.purgeAfter && ` · ${t('files.retention', { date: formatDate(file.purgeAfter.slice(0, 10), locale) })}`}
        </span>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        {file.sensitivity === 'shareable' || unlocking ? (
          <>
            {file.sensitivity === 'sensitive' && (
              <Input
                type="password"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder={t('files.field.unlockCode')}
                className="w-32"
                autoFocus
              />
            )}
            <Button
              variant="quiet"
              disabled={download.isPending}
              onClick={() => download.mutate(file.sensitivity === 'sensitive' ? code : undefined)}
            >
              {download.isPending
                ? t('files.downloading')
                : file.sensitivity === 'sensitive'
                  ? t('files.unlockSubmit')
                  : t('files.download')}
            </Button>
          </>
        ) : (
          <Button variant="quiet" onClick={() => setUnlocking(true)}>
            {t('files.enterCode')}
          </Button>
        )}
        <Button variant="danger" onClick={onRemove}>
          {t('common.remove')}
        </Button>
      </div>
      {download.isError && (
        <p className="text-xs text-red-600">
          {download.error instanceof ApiError && download.error.code === 'not_found'
            ? t('files.unlockFailed')
            : t('error.internal')}
        </p>
      )}
    </li>
  )
}
